import { ITranslations } from "../models"
import {
  IDropdownOption,
  IFieldState,
} from "../ui/product-form/_reducers/_utils/product-form-types"
import createControl, {
  isShorterThan,
  isLongerThan,
  isNumber,
  isHigherThan,
  isNotEmptyObject,
  isNotDroopyGuid,
  isCustomsCode,
  isNotDefaultObject,
  isNotMoreThan20Chars,
  hasSizeEqualTo,
  ensureVarietyOfCharacters,
  isCustomsCodeByOption,
  isNotMoreThan200Chars,
} from "./controls"

export const getErrorMessageFromEan = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const eanControl = createControl({
    // controls: [isLongerThan(9), isShorterThan(16), isNumber],
    controls: [hasSizeEqualTo(13), ensureVarietyOfCharacters, isNumber],
    errorMessage: translations.ean,
    localized,
  })

  return eanControl(value)
}

export const getErrorMessageFromPrice = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const priceControl = createControl({
    controls: [isHigherThan(0), isNumber],
    errorMessage: translations.price,
    localized,
  })

  return priceControl(value)
}

export const getErrorMessageFromField = (
  value: any,
  translations: any,
  localized: boolean,
  options: IDropdownOption[]
) => {
  const notEmptyControl = createControl({
    controls: [isNotEmptyObject],
    errorMessage: translations.field,
    localized,
  })

  return notEmptyControl(value)
}

export const getErrorMessageFromPurchaseLabel = (
  value: any,
  translations: any,
  localized: boolean,
  options: IDropdownOption[]
) => {
  const notEmptyControl = createControl({
    controls: [isNotEmptyObject, isNotMoreThan200Chars],
    errorMessage: translations.purchaseLabel,
    localized,
  })

  return notEmptyControl(value)
}

export const getErrorMessageFromSupplierRef = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const supplierRefControl = createControl({
    controls: [isNotEmptyObject, isNotDroopyGuid, isNotMoreThan20Chars],
    errorMessage: translations.supplierRef,
    localized,
  })

  return supplierRefControl(value)
}

export const getErrorMessageFromManufacturerRef = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const supplierRefControl = createControl({
    controls: [isNotDroopyGuid, isNotMoreThan20Chars],
    errorMessage: translations.manufacturerRef,
    localized,
  })

  return supplierRefControl(value)
}

export const getErrorMessageFromPictures = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const picturesControl = createControl({
    controls: [isNotEmptyObject],
    errorMessage: translations.pictures,
    localized,
  })

  return picturesControl(value)
}

export const getErrorMessageFromDeclination = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const declinedControl = createControl({
    controls: [isNotDefaultObject],
    errorMessage: translations.declination,
    localized,
  })

  return declinedControl(value)
}

export const getErrorMessageFromDeclined = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const declinedControl = createControl({
    controls: [isNotEmptyObject],
    errorMessage: translations.declined,
    localized,
  })

  return declinedControl(value)
}

export const getErrorMessageFromTree = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const nodeIsValid = (n: any) => !(n.father === "NULL" || n.name === "")

  const treeControl = createControl({
    controls: [nodeIsValid],
    errorMessage: translations.tree,
    localized,
  })

  return treeControl(value)
}

export const getErrorMessageFromCustomsCode = (
  value: any,
  translations: any,
  localized: boolean,
  options: IDropdownOption[]
) => {
  const customsCodeControl = createControl({
    controls: [isCustomsCode], //, isCustomsCodeByOption],
    errorMessage: translations.customsCode,
    localized,
  })

  return customsCodeControl(value)
}

export const getErrorMessageFromOriginCountry = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const countryControl = createControl({
    controls: [isNotEmptyObject, isNotDefaultObject],
    errorMessage: translations.originCountryIso2C,
    localized,
  })

  // tslint:disable-next-line
  return countryControl(value)
}

export const getErrorMessageFromBrand = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const brandControl = createControl({
    controls: [isNotEmptyObject, isNotDefaultObject],
    errorMessage: translations.brand,
    localized,
  })

  // tslint:disable-next-line
  return brandControl(value)
}

export const getErrorMessageFromVatRate = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const vatRateControl = createControl({
    controls: [isNotEmptyObject, isNotDefaultObject],
    errorMessage: translations.vatRate,
    localized,
  })

  // tslint:disable-next-line
  return vatRateControl(value)
}

export const getErrorMessageFromMinOrder = (
  value: any,
  translations: any,
  localized: boolean
) => {
  const minOrderControl = createControl({
    controls: [isHigherThan(0), isNumber],
    errorMessage: translations.minOrder,
    localized,
  })

  return minOrderControl(value)
}

export const getDroopyIdMessage = () => ""

const validators = {
  ean: getErrorMessageFromEan,
  name: getErrorMessageFromField,
  pictures: getErrorMessageFromPictures,
  price: getErrorMessageFromPrice,
  comNode: getErrorMessageFromTree,
  techNode: getErrorMessageFromTree,
  manufacturerRef: getErrorMessageFromManufacturerRef,
  supplierRef: getErrorMessageFromSupplierRef,
  buyingReasons: getErrorMessageFromField,
  purchaseLabel: getErrorMessageFromPurchaseLabel,
  declination: getErrorMessageFromDeclination,
  declined: getErrorMessageFromDeclined,
  minOrder: getErrorMessageFromMinOrder,
  droopyId: getDroopyIdMessage,
  originCountry: getErrorMessageFromOriginCountry,
  vatRate: getErrorMessageFromVatRate,
  customsCode: getErrorMessageFromCustomsCode,
  // brand: getErrorMessageFromBrand,
}

/**
 * This function takes a product and verifies if this product is valid. You need to pass the text for the errors here as the reducer can't be accessed through this function. If you won't display anu error messages, pass {} instead.
 * @param the IFieldProps Product
 * @param translations The object containing all the error messages to be displayed
 */
export const getErrorMessage = (
  {
    code,
    value = "",
    options,
    isRequired = validators[code],
    localized,
  }: IFieldState<any>,
  translations: ITranslations["fieldErrors"]
) => {
  if (isRequired || (value && !!validators[code])) {
    if (validators[code]) {
      return validators[code](value, translations, localized, options)
    }
    return getErrorMessageFromField(value, translations, localized, options)
  }
  return ""
}

export const isNullOrEmpty = (value: any) => {
  if (value === undefined) {
    return true
  }
  if (value === null) {
    return true
  }
  if (value.length <= 0) {
    return true
  }

  return false
}
